import { useCallback } from 'react'
import { useUrlState } from './useUrlState'
import { VodStatus, VodOrigin, VodAvailabilityState } from '../types/vod'

export interface VodsFilters {
  vodTypeCode: string
  origin: VodOrigin | ''
  availability: VodAvailabilityState | ''
  status: VodStatus | ''
  search: string
  matchId: string
  sortOrder: 'asc' | 'desc'
  page: number
}

/**
 * URL-backed filter state for the generalized VODs catalog (/vods).
 *
 * Separate from useVodFilters (the legacy highlights page) so the two can
 * evolve independently. Type is an open string because the option set comes
 * from the DB (GET /vod/types), not a hardcoded union.
 */
export function useVodsFilters() {
  const defaultFilters: VodsFilters = {
    vodTypeCode: '',
    origin: '',
    availability: '',
    status: '',
    search: '',
    matchId: '',
    sortOrder: 'desc',
    page: 1,
  }

  const { state, updateState } = useUrlState<VodsFilters>(defaultFilters, {
    vodTypeCode: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => value || '',
    },
    origin: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => (value as VodOrigin) || '',
    },
    availability: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => (value as VodAvailabilityState) || '',
    },
    status: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => (value as VodStatus) || '',
    },
    search: {
      serialize: (value) => (value && value.trim() ? value.trim() : undefined),
      deserialize: (value) => value || '',
    },
    matchId: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => value || '',
    },
    sortOrder: {
      serialize: (value) => (value !== 'desc' ? value : undefined),
      deserialize: (value) => (value as 'asc' | 'desc') || 'desc',
    },
    page: {
      serialize: (value) => (value > 1 ? String(value) : undefined),
      deserialize: (value) => (value ? parseInt(value, 10) : 1),
    },
  })

  /** Update filters and reset to page 1 */
  const updateFilterAndResetPage = useCallback(
    (updates: Partial<VodsFilters>) => {
      updateState({ ...updates, page: 1 })
    },
    [updateState]
  )

  /** Change page and scroll to top */
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateState({ page: newPage })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [updateState]
  )

  return {
    filters: state,
    updateFilters: updateState,
    updateFilterAndResetPage,
    handlePageChange,
  }
}
